import {
  Box,
  Button,
  Flex,
  Icon,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaDownload } from "react-icons/fa";
import {
  SeV2QuestionKind,
  SeV2QuestionStatus,
  useGetBuyerSupplierInformationQuery,
  useGetBuyerSeV2QuestionsQuery,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { InternalNotesSection } from "../InternalNotesSection";
import { BuyerQuestionInbox } from "./BuyerQuestionInbox";
import { ClarificationsPanel } from "./ClarificationsPanel";
import { downloadSeV2QaReport } from "./downloadSeV2QaReport";
import { InformationComposer } from "./InformationComposer";
import { PublicFeedPreview } from "./PublicFeedPreview";

export const CommunicationTab = ({ event }: { event: SourcingEventV2Dto }) => {
  const eventId = event.id;
  const eventTitle = event.eventData.title;
  const { t } = useTranslation();
  const displayer = useApiError();
  const [isDownloadingReport, setDownloadingReport] = useState(false);

  const downloadReport = async () => {
    setDownloadingReport(true);
    try {
      await downloadSeV2QaReport({
        eventId,
        fileName: `${eventTitle} - ${t("Questions and announcements")}`,
      });
    } catch (e) {
      displayer.trigger(e);
    } finally {
      setDownloadingReport(false);
    }
  };

  const {
    data: questionsData,
    error: questionsError,
    isLoading: questionsLoading,
  } = useGetBuyerSeV2QuestionsQuery({
    eventId,
  });
  const {
    data: announcementsData,
    error: announcementsError,
    isLoading: announcementsLoading,
  } = useGetBuyerSupplierInformationQuery({ eventId });

  useEffect(() => {
    if (questionsError) displayer.triggerSoft(questionsError);
  }, [questionsError, displayer]);
  useEffect(() => {
    if (announcementsError) displayer.triggerSoft(announcementsError);
  }, [announcementsError, displayer]);

  if (questionsLoading || announcementsLoading) {
    return (
      <Box py="10" textAlign="center">
        <Spinner color="smPrimary" />
      </Box>
    );
  }

  const threads = questionsData?.questions ?? [];
  // Supplier questions and buyer-opened clarification dialogs share an aggregate but not a tab: a question is
  // competition Q&A that may end up published to all, a clarification is a private 1:1 exchange (FOA § 23-5).
  const questions = threads.filter((q) => q.kind === SeV2QuestionKind.Question);
  const clarifications = threads.filter((q) => q.kind === SeV2QuestionKind.Clarification);
  const announcements = announcementsData?.supplierInformation ?? [];
  const unanswered = questions.filter(
    (q) => q.status === SeV2QuestionStatus.Unanswered,
  ).length;
  const awaitingTenderer = clarifications.filter(
    (q) => q.status === SeV2QuestionStatus.Unanswered,
  ).length;

  // Split announcements by source: an announcement is a published Q&A if a question points to it; otherwise standalone.
  const publishedInformationIds = new Set(
    questions
      .map((q) => q.publishedInformationId)
      .filter((id): id is string => !!id),
  );
  const qaAnnouncements = announcements.filter((a) =>
    publishedInformationIds.has(a.id),
  );
  const infoAnnouncements = announcements.filter(
    (a) => !publishedInformationIds.has(a.id),
  );

  return (
    <Tabs colorScheme="teal" isLazy>
      <Flex justify="flex-end" pb="2">
        <Button
          size="sm"
          variant="ghost"
          colorScheme="blue"
          leftIcon={<Icon as={FaDownload} />}
          isLoading={isDownloadingReport}
          onClick={downloadReport}
        >
          {t("Download Q&A report")}
        </Button>
      </Flex>
      <TabList>
        <Tab>
          {t("Questions")}
          {unanswered > 0 ? ` (${unanswered})` : ""}
        </Tab>
        <Tab>
          {t("Public Q&A")}
          {qaAnnouncements.length > 0 ? ` (${qaAnnouncements.length})` : ""}
        </Tab>
        <Tab>
          {t("Information to tenderers")}
          {infoAnnouncements.length > 0 ? ` (${infoAnnouncements.length})` : ""}
        </Tab>
        <Tab>
          {t("Clarifications")}
          {awaitingTenderer > 0 ? ` (${awaitingTenderer})` : ""}
        </Tab>
        <Tab>
          {t("Internal conversation")}
          {(event.internalConversation?.length ?? 0) > 0 ? ` (${event.internalConversation?.length})` : ""}
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel px="0">
          <BuyerQuestionInbox eventId={eventId} questions={questions} />
        </TabPanel>
        <TabPanel px="0">
          <PublicFeedPreview eventId={eventId} announcements={qaAnnouncements} />
        </TabPanel>
        <TabPanel px="0">
          <InformationComposer
            eventId={eventId}
            announcements={infoAnnouncements}
          />
        </TabPanel>
        <TabPanel px="0">
          <ClarificationsPanel event={event} questions={clarifications} />
        </TabPanel>
        <TabPanel px="0">
          <InternalNotesSection event={event} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
